import { CommonHelperFunctions } from "@kuika/kuika-cl-model";
import * as React from "react";
import { AgentTokenService, type IssuedAgentTokenResponse } from "../../../../../services/agent-token-service";
import { KModal } from "../../../../ui-kit/k-modal/k-modal";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaModalIconWrapper from "../../../../ui-kit/kuika-modal-icon-wrapper/kuika-modal-icon-wrapper";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaH5 from "../../../../ui-kit/typography/H5/H5";
import { buildRemoveCommand, buildSetupCommand, formatAgentTokenDate, MAX_LABEL_LENGTH } from "../agent-token-utils";
import { extractBackendError } from "../ai-chatbot-utils";

// ── Create an agent token, then reveal it ONCE ──────────────────────────────
// Two steps in one modal: name it → the server issues it → the token is shown
// exactly once. It lives only in this component's state — never in a store,
// localStorage, a log or the URL — and is gone when the modal closes. Closing
// on the reveal step is therefore deliberate-only (no scrim/escape close).

interface AgentTokenCreateModalProps {
  onClose: () => void;
  /** Called once a token exists, so the parent list refetches behind the reveal step. */
  onCreated: () => void | Promise<void>;
}

interface State {
  label: string;
  creating: boolean;
  error: string | null;
  issued: IssuedAgentTokenResponse | null;
  copied: "token" | "command" | "remove" | null;
}

const COPIED_FEEDBACK_MS: number = 2000;

class AgentTokenCreateModal extends React.PureComponent<AgentTokenCreateModalProps, State> {
  state: State = { label: "", creating: false, error: null, issued: null, copied: null };

  private isMounted_: boolean = false;
  private copiedTimer: ReturnType<typeof setTimeout> | null = null;

  componentDidMount(): void {
    this.isMounted_ = true;
  }

  componentWillUnmount(): void {
    this.isMounted_ = false;
    if (this.copiedTimer) clearTimeout(this.copiedTimer);
  }

  private handleCreate = async (): Promise<void> => {
    if (this.state.creating) return;
    this.setState({ creating: true, error: null });
    try {
      const issued: IssuedAgentTokenResponse = (await AgentTokenService.Create(this.state.label)).data;
      if (!this.isMounted_) return;
      this.setState({ issued, creating: false });
      void this.props.onCreated();
    } catch (err: unknown) {
      if (!this.isMounted_) return;
      this.setState({ creating: false, error: describeCreateError(err) });
    }
  };

  private handleCopy = async (kind: "token" | "command" | "remove", text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      this.setState({ error: "Copy failed — select the text and copy it manually." });
      return;
    }
    this.setState({ copied: kind, error: null });
    if (this.copiedTimer) clearTimeout(this.copiedTimer);
    this.copiedTimer = setTimeout((): void => {
      if (this.isMounted_) this.setState({ copied: null });
    }, COPIED_FEEDBACK_MS);
  };

  private renderError(): React.ReactNode {
    const { error } = this.state;
    if (!error) return null;
    return (
      <div className="aichat-agent-tokens__modal-error" role="alert">
        {error}
      </div>
    );
  }

  private renderNameStep(title: string): React.ReactNode {
    const { label, creating } = this.state;
    return (
      <div className="aichat-agent-tokens__modal" data-testid="agentTokenCreateStep">
        <div className="aichat-agent-tokens__modal-header">
          <KuikaModalIconWrapper iconName="key" intent="create" />
          <KuikaH5>{title}</KuikaH5>
        </div>
        <p className="aichat-agent-tokens__modal-hint">
          An agent token lets Claude Code work in <strong>your</strong> designer session, with your permissions. It
          expires after 90 days and you can revoke it at any time.
        </p>
        <div className="aichat-agent-tokens__field">
          <label className="aichat-agent-tokens__field-label" htmlFor="agentTokenLabel">
            Name <span className="aichat-agent-tokens__field-optional">· optional</span>
          </label>
          <KuikaInput
            id="agentTokenLabel"
            autoFocus
            placeholder="e.g. Work laptop"
            value={label}
            maxLength={MAX_LABEL_LENGTH}
            disabled={creating}
            onChange={(_e: unknown, value: string | number): void =>
              this.setState({ label: String(value).slice(0, MAX_LABEL_LENGTH) })
            }
            onPressEnter={this.handleCreate}
            data-testid="agentTokenLabelInput"
          />
          <p className="aichat-agent-tokens__field-help">
            Name it after the device, so you know which one to revoke if it is lost.
          </p>
        </div>
        {this.renderError()}
      </div>
    );
  }

  private renderRevealStep(issued: IssuedAgentTokenResponse, title: string): React.ReactNode {
    const { copied } = this.state;
    const command: string = buildSetupCommand(CommonHelperFunctions.GetAiChatUrl(), issued.agentToken);
    const removeCommand: string = buildRemoveCommand();
    return (
      <div className="aichat-agent-tokens__modal" data-testid="agentTokenRevealStep">
        <div className="aichat-agent-tokens__modal-header">
          <KuikaModalIconWrapper iconName="circle-check" intent="success" />
          <KuikaH5>{title}</KuikaH5>
        </div>
        <div className="aichat-agent-tokens__notice" role="status">
          <KuikaSvg width={16} height={16} name="circle-warning" type="common" />
          <span>
            Copy it now — <strong>this is the only time the token is shown.</strong> Kuika does not store it. Treat it
            like a password: it acts as you until {formatAgentTokenDate(issued.expiresAt)}.
          </span>
        </div>

        <div className="aichat-agent-tokens__field">
          <span className="aichat-agent-tokens__field-label">Claude Code setup</span>
          <div className="aichat-agent-tokens__secret aichat-agent-tokens__secret--block aichat-agent-tokens__secret--primary">
            <pre className="aichat-agent-tokens__secret-value" data-testid="agentTokenSetupCommand">
              {command}
            </pre>
            <KuikaButton
              type="primary"
              size="sm"
              onClick={(): Promise<void> => this.handleCopy("command", command)}
              data-testid="agentTokenCopyCommand"
            >
              {copied === "command" ? "Copied" : "Copy command"}
            </KuikaButton>
          </div>
          <p className="aichat-agent-tokens__field-help">
            Run it once in a terminal, then start Claude Code. It works in every project on this device.
          </p>
        </div>

        <div className="aichat-agent-tokens__field">
          <span className="aichat-agent-tokens__field-label">
            Replacing a token <span className="aichat-agent-tokens__field-optional">· only if needed</span>
          </span>
          <div className="aichat-agent-tokens__secret">
            <code className="aichat-agent-tokens__secret-value" data-testid="agentTokenRemoveCommand">
              {removeCommand}
            </code>
            <KuikaButton
              type="secondary"
              size="sm"
              onClick={(): Promise<void> => this.handleCopy("remove", removeCommand)}
              data-testid="agentTokenCopyRemove"
            >
              {copied === "remove" ? "Copied" : "Copy"}
            </KuikaButton>
          </div>
          <p className="aichat-agent-tokens__field-help">
            If Claude Code reports <em>"kuika-unified already exists"</em>, run this first, then the command above.
          </p>
        </div>

        <div className="aichat-agent-tokens__field">
          <span className="aichat-agent-tokens__field-label">
            Agent token <span className="aichat-agent-tokens__field-optional">· already in the command</span>
          </span>
          <div className="aichat-agent-tokens__secret">
            <code className="aichat-agent-tokens__secret-value" data-testid="agentTokenValue">
              {issued.agentToken}
            </code>
            <KuikaButton
              type="secondary"
              size="sm"
              onClick={(): Promise<void> => this.handleCopy("token", issued.agentToken)}
              data-testid="agentTokenCopy"
            >
              {copied === "token" ? "Copied" : "Copy"}
            </KuikaButton>
          </div>
        </div>
        {this.renderError()}
      </div>
    );
  }

  render(): React.ReactNode {
    const { issued, creating } = this.state;
    const { onClose } = this.props;
    const title: string = issued ? "Your new agent token" : "Create agent token";

    return (
      <KModal
        showModal
        header={title}
        width={600}
        centered
        hideCloseIcon={issued !== null}
        closeModal={issued ? undefined : onClose}
        footer={
          <div className="aichat-agent-tokens__modal-footer">
            {issued ? (
              <KuikaButton type="primary" size="md" onClick={onClose} data-testid="agentTokenDone">
                I've copied it
              </KuikaButton>
            ) : (
              <>
                <KuikaButton type="secondary" size="md" disabled={creating} onClick={onClose}>
                  Cancel
                </KuikaButton>
                <KuikaButton
                  type="primary"
                  size="md"
                  loading={creating}
                  disabled={creating}
                  onClick={this.handleCreate}
                  data-testid="agentTokenCreateSubmit"
                >
                  Create token
                </KuikaButton>
              </>
            )}
          </div>
        }
      >
        {issued ? this.renderRevealStep(issued, title) : this.renderNameStep(title)}
      </KModal>
    );
  }
}

/** Server reason codes → copy a user can act on. Never echoes request data. */
export function describeCreateError(err: unknown): string {
  const message: string = extractBackendError(err);
  if (message === "too_many_active_tokens") {
    return "You have reached the limit of 20 active tokens. Revoke one you no longer use, then try again.";
  }
  if (message === "disabled" || /MCP_BRIDGE_TOKEN_SECRET|Agent tokens are disabled/i.test(message)) {
    return "Agent tokens are not enabled on this Kuika server yet. Please contact your administrator.";
  }
  return message;
}

export default AgentTokenCreateModal;
